/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: Feature.ts
 * Project: mle-client-n
 * Committed last: 2022/1/14 @ 845
 * ---
 * Description:
 */

export interface IFeature<T, D, Z> {
  target: T;
  dataSelector: (source: Z) => D | undefined;
}

/**
 * Represents a feature into the work-area. Every feature should be
 * observable by default. They can define some behaviors and mutable
 * states.
 *
 * The **target** is an object to operate onto. For example zoom operates
 * directly on the stage.
 */
export abstract class Feature<TargetType, DataType, SourceType> implements IFeature<TargetType, DataType, SourceType> {
  // Target of this feature (object to operate onto)
  target: TargetType;

  // Name of this feature
  featureName: string;
  // Pointer to the source of this feature
  sourcePointer: SourceType;

    /**
     * Init this feature.
     * @param name Feature name
     * @param tPointer Pointer to the target
     * @param sourcePointer Pointer to the whole work-area
     * @param active Enable / disable this feature
     * @protected
     */
  protected constructor(name: string, tPointer: TargetType, sourcePointer: SourceType, active: boolean) {
    this.featureName = name;
    this.target = tPointer;
    this.sourcePointer = sourcePointer;
    this.enabled = active;
  }

  // Enable / disable this feature (by default it is enabled)
  private enabled: boolean;

  // TODO: Implement use of dataSelector
  dataSelector(source: SourceType): DataType | undefined {
    return undefined;
  }

  /**
   * Initialize this functionality block. Called by FeaturesManager only if this
   * feature is enabled. Should return true if everything is ok, false otherwise
   */
  abstract init(initData?: DataType): boolean;

    /**
     * Read data from work-area using selector
     */
  getData(): DataType | undefined {
    return this.dataSelector(this.sourcePointer);
  }

  /**
   * Enable this feature (by default it already is)
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * Disable this feature. It won't be initialized or returned from any
   * function.
   */
  disable(): void {
    this.enabled = false;
    // Destroy feature
    this.destroy();
  }

  /**
   * Check if this feature is enabled or not
   */
  isEnabled(): boolean {
    return this.enabled
  }

  /**
   * Called when this feature needs to be destroyed
   * or gets disabled.
   */
  abstract destroy(): boolean;

  toString(): string {
      return `Feature ${this.featureName}`;
  }
}